import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Sparkles, Loader2, History, Truck, Search } from "lucide-react";
import { toast } from "sonner";
import { usePaginacao } from "../hooks/usePaginacao";
import { Paginacao } from "../components/Paginacao";
import { EmptyState, SkeletonTabela } from "../components/EmptyState";
import { ExportarBotao } from "../components/ExportarBotao";

const STATUS_LABEL = {
  disponivel: "Disponível",
  locada: "Locada",
  oficina: "Oficina",
  extraviada: "Extraviada",
};

export default function Frota() {
  const [modelos, setModelos] = useState([]);
  const [aberta, setAberta] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [analise, setAnalise] = useState(null);
  const [loadingAnalise, setLoadingAnalise] = useState(false);

  const {
    itens: unidades, total, pagina, totalPaginas, limite,
    carregando, filtros, irParaPagina, setFiltro,
  } = usePaginacao("/unidades", {
    limite: 20,
    filtrosIniciais: { q: "", status: "", modelo_id: "" },
  });

  useEffect(() => {
    api.get("/modelos").then((r) => setModelos(r.data));
  }, []);

  const modeloMap = useMemo(() => Object.fromEntries(modelos.map((m) => [m.id, m])), [modelos]);

  const abrir = async (u) => {
    setAberta(u);
    setAnalise(null);
    const { data } = await api.get(`/unidades/${u.id}/eventos`);
    setEventos(data);
  };

  const analisar = async () => {
    if (!aberta) return;
    setLoadingAnalise(true);
    try {
      const { data } = await api.post("/ai/analise-unidade", { unidade_id: aberta.id });
      setAnalise(data);
      toast.success("Análise IA pronta");
    } finally {
      setLoadingAnalise(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Frota</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total.toLocaleString("pt-BR")} unidades monitoradas
          </p>
        </div>
        <ExportarBotao endpoint="/export/unidades" filtros={filtros} nomeArquivo="frota" testId="exportar-frota" />
      </div>

      <Card className="p-3 flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Buscar por número de série…"
            value={filtros.q || ""}
            onChange={(e) => setFiltro("q", e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="frota-busca"
          />
        </div>
        <Select value={filtros.status || "all"} onValueChange={(v) => setFiltro("status", v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 h-9" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtros.modelo_id || "all"} onValueChange={(v) => setFiltro("modelo_id", v === "all" ? "" : v)}>
          <SelectTrigger className="w-56 h-9" data-testid="filter-modelo">
            <SelectValue placeholder="Modelo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os modelos</SelectItem>
            {modelos.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      <Card>
        {carregando ? (
          <SkeletonTabela linhas={10} colunas={5} />
        ) : unidades.length === 0 ? (
          <EmptyState
            icone={Truck}
            titulo="Nenhuma unidade encontrada"
            descricao={filtros.q ? `Nenhum resultado para "${filtros.q}".` : "Nenhum equipamento na frota com os filtros atuais."}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº Série</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Fabricante</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unidades.map((u) => (
                  <TableRow key={u.id} data-testid={`unidade-row-${u.id}`}>
                    <TableCell className="font-mono text-xs">{u.numero_serie}</TableCell>
                    <TableCell>{modeloMap[u.modelo_id]?.nome || "—"}</TableCell>
                    <TableCell>{modeloMap[u.modelo_id]?.fabricante || "—"}</TableCell>
                    <TableCell>
                      <span className={`status-dot status-${u.status}`} />
                      {STATUS_LABEL[u.status] || u.status}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => abrir(u)} data-testid={`balanco-btn-${u.id}`}>
                        <History size={12} className="mr-1" /> Balanço
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="px-4">
              <Paginacao
                pagina={pagina}
                totalPaginas={totalPaginas}
                total={total}
                limite={limite}
                irParaPagina={irParaPagina}
              />
            </div>
          </>
        )}
      </Card>

      <Sheet open={!!aberta} onOpenChange={(o) => !o && setAberta(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              Balanço — {aberta?.numero_serie}
              <div className="text-xs text-muted-foreground font-normal mt-1">
                {modeloMap[aberta?.modelo_id]?.nome}
              </div>
            </SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-4">
            <Button data-testid="analisar-ia-btn" onClick={analisar} disabled={loadingAnalise} className="w-full text-white" style={{ background: "#F97316" }}>
              {loadingAnalise ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
              Analisar com IA (Claude)
            </Button>
            {analise && (
              <Card className="p-4 border-l-4" style={{ borderLeftColor: "#1B3A6B" }} data-testid="analise-ia-output">
                <h4 className="font-bold mb-2" style={{ color: "#1B3A6B" }}>Análise Claude</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Vida útil restante:</strong> {analise.vida_util_restante}</div>
                  <div><strong>Padrão de falhas:</strong> {analise.padrao_falhas}</div>
                  <div><strong>Recomendação:</strong> <Badge>{analise.recomendacao}</Badge></div>
                </div>
              </Card>
            )}
            <div>
              <h4 className="font-bold mb-2" style={{ color: "#1B3A6B" }}>Linha do tempo</h4>
              <div className="space-y-2">
                {eventos.length === 0 && <p className="text-sm text-muted-foreground">Sem eventos.</p>}
                {eventos.map((e) => (
                  <div key={e.id} className="border-l-2 pl-3 pb-2" style={{ borderColor: "#F97316" }}>
                    <div className="text-xs text-muted-foreground">{new Date(e.data).toLocaleString("pt-BR")}</div>
                    <div className="text-sm">
                      <Badge variant="outline" className="mr-2 text-[10px]">{e.tipo}</Badge>
                      {e.descricao}
                    </div>
                    {e.custo > 0 && <div className="text-xs text-muted-foreground">Custo: R$ {e.custo.toFixed(2)}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
